import { FC, useMemo } from 'react';
import { Stack } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { getUserTimeline } from '@/lib/services/user-event.service';
import { useInfiniteQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { getNiceDate, getUserTimezone } from '@/lib/helpers';
import useQueryHelper from '@/hooks/use-query-helper';
import Loader from '@/components/loader';
import InlineToast from '@/components/toasts/inline';
import Timeline, { TimelineGroup } from '@/components/timeline';
import { getDateSectionLabel } from '@/components/timeline/helpers';
import SessionItem from './session-item';
import { groupEventsByServiceRun } from './helpers';
import Chip from '@/components/chip';
import Button from '@/components/button';

interface Props {
  userId: string;
}

const useStyles = createUseStyles({
  dateChip: {
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    pointerEvents: 'none',
  },
});

const UserTimeline: FC<Props> = ({ userId }) => {
  const styles = useStyles();
  const query = useInfiniteQuery(
    [QueryKey.UserTimelineDataset, userId],
    ({ pageParam }) => getUserTimeline(userId, (pageParam as string) || ''),
    {
      enabled: !!userId,
      getNextPageParam: ({ nextCursor }) => nextCursor || undefined,
      // Override the global 0 cacheTime / 4s staleTime so accumulated "Load more"
      // pages survive a tab switch instead of resetting to the first page.
      cacheTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
    }
  );
  const { showLoader } = useQueryHelper(query);
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  const rows = useMemo(() => data?.pages.flatMap(({ rows }) => rows) ?? [], [data]);

  const handleClickLoadMore = () => {
    fetchNextPage();
  };

  const timelineGroups = useMemo<TimelineGroup[]>(() => {
    const { timezone } = getUserTimezone();
    const groupedEvents: Record<string, UserEventModel[]> = {};
    rows.forEach((event) => {
      const { date: dateKey } = getNiceDate(event.startTime, undefined, timezone);
      if (!groupedEvents[dateKey]) {
        groupedEvents[dateKey] = [];
      }
      groupedEvents[dateKey].push(event);
    });

    // ISO yyyy-MM-dd keys sort lexicographically; reverse for newest-first.
    const sortedDateKeys = Object.keys(groupedEvents).sort().reverse();

    return sortedDateKeys.map((dateKey) => {
      const sessions = groupEventsByServiceRun(groupedEvents[dateKey]);
      return {
        key: dateKey,
        header: (
          <Chip
            label={getDateSectionLabel(dateKey)}
            size="small"
            color="info"
            className={styles.dateChip}
          />
        ),
        items: sessions.map((session, sessionIndex) => (
          <SessionItem
            key={`${dateKey}-${sessionIndex}`}
            session={session}
            isLastInGroup={sessionIndex === sessions.length - 1}
          />
        )),
      };
    });
  }, [rows, styles]);

  if (showLoader) {
    return <Loader />;
  }
  if (!timelineGroups.length) {
    return <InlineToast message="No events found for this user" level="info" />;
  }

  return (
    <Stack gap={2}>
      <Timeline groups={timelineGroups} showEndMessage={!hasNextPage} />
      {hasNextPage && (
        <Stack alignItems="center">
          <Button
            label="Load More"
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
            onClick={handleClickLoadMore}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default UserTimeline;
